import { z } from "zod";
import { pageQuery, phone, trimmed } from "./common";
import { CUSTOMER_SEGMENTS } from "./crm";

/**
 * SMS-рассылки (`addon.sms` + квота `quota.sms` в месяц, ТЗ 16 §16.1, ТЗ 03 BR-SaaS-11): клиентам с согласием на рассылки,
 * по сегменту CRM или тегу. Квота — SMS-части за календарный месяц: тариф + пакеты «SMS 1000».
 */

/** Алфавит GSM-7 (латиница, цифры, базовые знаки) — 160 символов в SMS; любой другой символ (кириллица) — UCS-2, 70. */
const GSM = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXT = "^{}\\[~]|€";

/** Сколько SMS-частей займёт текст: GSM-7 — 160 (в склейке 153), UCS-2 — 70 (67). */
export function smsParts(text: string): { encoding: "gsm7" | "ucs2"; length: number; parts: number; perPart: number } {
  const gsm = [...text].every((ch) => GSM.includes(ch) || GSM_EXT.includes(ch));
  if (gsm) {
    const length = [...text].reduce((s, ch) => s + (GSM_EXT.includes(ch) ? 2 : 1), 0);
    const parts = length <= 160 ? 1 : Math.ceil(length / 153);
    return { encoding: "gsm7", length, parts: Math.max(parts, 1), perPart: parts > 1 ? 153 : 160 };
  }
  const length = [...text].length;
  const parts = length <= 70 ? 1 : Math.ceil(length / 67);
  return { encoding: "ucs2", length, parts: Math.max(parts, 1), perPart: parts > 1 ? 67 : 70 };
}

/** Длинные рассылки дорогие: не больше 5 частей (≈ 335 символов кириллицей) */
export const SMS_MAX_PARTS = 5;
const smsText = trimmed(1, 765).refine((t) => smsParts(t).parts <= SMS_MAX_PARTS, { error: `Слишком длинно: не больше ${SMS_MAX_PARTS} SMS` });

export const smsAudienceSchema = z.strictObject({
  segment: z.enum(CUSTOMER_SEGMENTS).nullable().optional(),
  /** Пустое поле формы — «без тега» */
  tag: z.preprocess((v) => (typeof v === "string" && !v.trim() ? null : v), trimmed(1, 30).nullable().optional()),
});
export const smsCampaignSchema = smsAudienceSchema.extend({ name: trimmed(2, 120), text: smsText });
export type SmsCampaignInput = z.input<typeof smsCampaignSchema>;
export type SmsCampaign = z.output<typeof smsCampaignSchema>;
export const smsPreviewSchema = smsAudienceSchema.extend({ text: smsText });
export const smsTestSchema = z.strictObject({ phone, text: smsText });
export const smsCampaignsQuery = pageQuery;

export const SMS_STATUSES = ["queued", "sent", "failed", "skipped", "quota_exceeded"] as const;
export type SmsStatus = (typeof SMS_STATUSES)[number];
